import { EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../constants"

/**
 * Determines which deactivate/delete modal mode to use based on equipment data
 * @param {Object} equipmentDetails - Detailed equipment data from API
 * @param {string} equipmentDetails.mode - Direct mode from API (preferred)
 * @param {string} equipmentDetails.status - API status: "no_experiences_assigned" | "no_active_bookings" | "fully_substitutable" | "partially_substitutable"
 * @param {Array} equipmentDetails.assigned_trips - Array of assigned trips
 * @param {Array} equipmentDetails.active_bookings - Array of active bookings
 * @returns {string} One of EQUIPMENT_DELETE_DEACTIVATE_MODES
 */
export const determineDeactivateMode = (equipmentDetails) => {
  const {
    mode,
    status,
    assigned_trips = [],
    active_bookings = []
  } = equipmentDetails || {}

  // Priority 1: If API provides mode field directly, use it
  if (mode) {
    return mode
  }

  // Priority 2: If API provides status field, map it to mode
  if (status) {
    const statusModeMap = {
      no_experiences_assigned: EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES,
      no_active_bookings: EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING,
      fully_substitutable: EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE,
      partially_substitutable: EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE
    }
    return statusModeMap[status] || EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  }

  // Fallback: Calculate mode from data if status not provided
  const assignedTripsCount = assigned_trips.length
  const activeBookingsCount = active_bookings.length

  // Scenario 1: No experiences assigned, no bookings
  if (assignedTripsCount === 0 && activeBookingsCount === 0) {
    return EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  }

  // Scenario 2: Has assigned experiences but no active bookings
  if (assignedTripsCount > 0 && activeBookingsCount === 0) {
    return EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING
  }

  // Scenario 3 & 4: Has upcoming bookings
  if (activeBookingsCount > 0) {
    // Check if all bookings can be automatically substituted
    const allBookingsSubstitutable = active_bookings.every(
      (booking) => booking.can_substitute === true
    )

    if (allBookingsSubstitutable) {
      return EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE
    } else {
      return EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE
    }
  }

  // Default to no experiences mode
  return EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
}
